import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  useUploadTechnicianPhoto,
  type TechnicianProfile,
} from '@/routes/technicians/useTechnicianProfile';

/**
 * A technician's profile photo, owner/admin only (M1-24) — the AC carves out
 * only contact/emergency fields for self-edit, so this is not offered to a
 * technician viewing their own profile, even though uploading one's own
 * headshot would seem harmless. Literal AC over intuition.
 */
export function TechnicianPhotoUpload({ profile }: { profile: TechnicianProfile }) {
  const { t } = useTranslation();
  const upload = useUploadTechnicianPhoto(profile.id);
  const inputRef = useRef<HTMLInputElement>(null);

  function onFileChosen(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) {
      upload.mutate(file);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {profile.photoThumbnailUrl ? (
        <img
          src={profile.photoThumbnailUrl}
          alt={t('technicianProfile.photo.alt')}
          className="h-16 w-16 rounded-full object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-muted)] text-xs text-[var(--color-muted-foreground)]"
        >
          {t('technicianProfile.photo.none')}
        </div>
      )}
      <div>
        {/* ⚠️ Hidden from the accessibility tree AND taken out of the tab
            order (M1-47). `sr-only` clips this to 1×1px but leaves it
            FOCUSABLE, so it was the first tab stop on this screen — ahead of
            the Button that is actually labelled "Upload photo", and with no
            usable focus indicator, so focus appeared to vanish for one press.
            Measured before the fix: the tab order read
            [INPUT[type=file], BUTTON[type=button]].

            It takes both attributes: an element hidden from the accessibility
            tree must not be focusable, which is what axe's published
            `aria-hidden-focus` rule is for. ⚠️ That rule cannot be relied on
            HERE, though — under jsdom every element reports zero dimensions,
            so axe never treats an `sr-only` input as focusable and the rule
            does not fire. Proved in the review of #114 rather than assumed
            from the docs. The guard is the assertion in the test, not the
            scan.

            ⚠️ The wrapping <label> is gone with it: a label is meaningless on
            an element no assistive technology can reach, and it was only ever
            there to satisfy axe's label rule. The Button is the real control —
            it carries the name, the disabled state and the click. Same shape
            as `VehiclePhotoPanel` (M1-44), where this was first got right. */}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          data-testid="technician-photo-input"
          aria-hidden="true"
          tabIndex={-1}
          onChange={onFileChosen}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {upload.isPending
            ? t('technicianProfile.photo.uploading')
            : t('technicianProfile.photo.upload')}
        </Button>
        {upload.isError ? (
          <p role="alert" className="mt-1 text-xs text-[var(--color-destructive)]">
            {t('technicianProfile.photo.failed')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
